import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Clock, Loader2, CalendarCheck, ChevronLeft, ChevronRight, MapPin, Info, CheckCircle2, Sparkles, Plus } from "lucide-react";
import { api, LOGO_URL, formatApiErrorDetail, euro } from "../../lib/api";
import { toast } from "sonner";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths,
  format, isSameMonth, isBefore, startOfToday, isSameDay,
} from "date-fns";
import { nl } from "date-fns/locale";

const CATEGORY_ORDER = ["Gelnagels", "Bijwerking – eigen werk", "Verwijdering"];
const LABELS = { treatment: "Behandeling", extras: "Extra's", date: "Datum", time: "Tijd", details: "Gegevens", summary: "Bevestiging" };

export default function Booking() {
  const nav = useNavigate();
  const [services, setServices] = useState([]);
  const [addons, setAddons] = useState([]);
  const [service, setService] = useState(null);
  const [selAddons, setSelAddons] = useState([]);
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [durationConfigured, setDurationConfigured] = useState(true);
  const [time, setTime] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [idx, setIdx] = useState(0);
  const [success, setSuccess] = useState(false);

  const flow = useMemo(
    () => (addons.length ? ["treatment", "extras", "date", "time", "details", "summary"] : ["treatment", "date", "time", "details", "summary"]),
    [addons.length]
  );
  const step = success ? "success" : flow[idx];

  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
    api.get("/addons").then(({ data }) => setAddons(data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  const addonIds = selAddons.join(",");
  useEffect(() => {
    if (step === "time" && service && date) {
      setSlotsLoading(true);
      setTime(null);
      api.get(`/availability/slots`, { params: { service_id: service.id, date: format(date, "yyyy-MM-dd"), addon_ids: addonIds } })
        .then(({ data }) => { setSlots(data.slots); setDurationConfigured(data.duration_configured); })
        .catch(() => setSlots([]))
        .finally(() => setSlotsLoading(false));
    }
  }, [step, service, date, addonIds]);

  const next = () => { setIdx((i) => i + 1); window.scrollTo(0, 0); };
  const back = () => { if (idx === 0) nav("/"); else { setIdx((i) => i - 1); window.scrollTo(0, 0); } };

  const grouped = CATEGORY_ORDER.map((cat) => ({ cat, items: services.filter((s) => s.category === cat) })).filter((g) => g.items.length);
  const selectedAddonObjs = addons.filter((a) => selAddons.includes(a.id));
  const total = (service?.price || 0) + selectedAddonObjs.reduce((s, a) => s + a.price, 0);

  const toggleAddon = (id) => setSelAddons((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        service_id: service.id, date: format(date, "yyyy-MM-dd"), start_time: time,
        addon_ids: selAddons, ...form,
      });
      setConfirmed(data); setSuccess(true); window.scrollTo(0, 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      if (e.response?.status === 409) { setSuccess(false); setIdx(flow.indexOf("time")); }
    } finally { setSubmitting(false); }
  };

  const validDetails = form.first_name && form.last_name && form.email && form.phone;

  const gridStart = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
  const gridEnd = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
  const days = [];
  for (let d = gridStart; !isBefore(gridEnd, d); d = addDays(d, 1)) days.push(d);
  const tomorrow = addDays(startOfToday(), 1);

  return (
    <div className="min-h-screen bg-brand-neutral">
      <header className="bg-white/90 backdrop-blur-md border-b border-brand-border sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="booking-home-link">
            <img src={LOGO_URL} alt="Nails Renss" className="h-9 w-9 rounded-full" />
            <span className="font-display text-xl font-semibold text-brand-charcoal">Nails Renss</span>
          </Link>
          {!success && (
            <button onClick={back} className="flex items-center gap-1 text-sm text-brand-muted hover:text-brand-charcoal" data-testid="booking-back-button">
              <ArrowLeft size={16} /> Terug
            </button>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8 pb-16">
        {!success && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              {flow.map((s, i) => (
                <div key={s} className="flex items-center flex-1 last:flex-none">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 transition-colors ${idx > i ? "bg-brand-pink text-white" : idx === i ? "bg-brand-charcoal text-white" : "bg-white border border-brand-border text-brand-muted"}`}>
                    {idx > i ? <Check size={14} /> : i + 1}
                  </div>
                  {i < flow.length - 1 && <div className={`h-0.5 flex-1 mx-1 ${idx > i ? "bg-brand-pink" : "bg-brand-border"}`} />}
                </div>
              ))}
            </div>
            <p className="text-center font-display text-3xl font-semibold text-brand-charcoal mt-5">
              {step === "treatment" && "Kies je behandeling"}
              {step === "extras" && "Extra's"}
              {step === "date" && "Kies een datum"}
              {step === "time" && "Kies een tijdstip"}
              {step === "details" && "Jouw gegevens"}
              {step === "summary" && "Overzicht & bevestiging"}
            </p>
          </div>
        )}

        {step === "treatment" && (
          <div className="space-y-8 animate-fade-up" data-testid="booking-step-treatment">
            {grouped.map((g) => (
              <div key={g.cat}>
                <h3 className="eyebrow mb-3">{g.cat}</h3>
                <div className="space-y-2.5">
                  {g.items.map((s) => {
                    const bookable = !!s.duration_minutes;
                    return (
                      <button key={s.id} disabled={!bookable} onClick={() => { setService(s); next(); }} data-testid={`treatment-option-${s.id}`}
                        className={`w-full text-left bg-white rounded-2xl border p-4 flex items-center justify-between gap-4 transition-all ${bookable ? "border-brand-border hover:border-brand-pink hover:shadow-sm" : "border-brand-border/60 opacity-60 cursor-not-allowed"}`}>
                        <div>
                          <p className="font-medium text-brand-charcoal">{s.name}</p>
                          {s.description && <p className="text-sm text-brand-muted mt-0.5">{s.description}</p>}
                          {bookable ? <span className="inline-flex items-center gap-1 text-xs text-brand-muted mt-1"><Clock size={12} /> {s.duration_minutes} min</span>
                            : <span className="inline-block text-xs text-brand-pinkhover mt-1">Nog niet online boekbaar — bel ons</span>}
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="font-display text-xl font-semibold text-brand-charcoal">{euro(s.price)}</span>
                          {bookable && <ArrowRight size={18} className="text-brand-pink" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {step === "extras" && (
          <div className="animate-fade-up" data-testid="booking-step-extras">
            <p className="text-center text-brand-muted mb-5">Wil je iets toevoegen aan je behandeling? (optioneel)</p>
            <div className="space-y-2.5">
              {addons.map((a) => {
                const checked = selAddons.includes(a.id);
                return (
                  <button key={a.id} onClick={() => toggleAddon(a.id)} data-testid={`addon-option-${a.id}`}
                    className={`w-full text-left bg-white rounded-2xl border p-4 flex items-center justify-between gap-4 transition-all ${checked ? "border-brand-pink ring-1 ring-brand-pink" : "border-brand-border hover:border-brand-pink"}`}>
                    <div className="flex items-center gap-3">
                      <span className={`h-6 w-6 rounded-md border flex items-center justify-center ${checked ? "bg-brand-pink border-brand-pink" : "border-brand-border"}`}>
                        {checked && <Check size={15} className="text-white" />}
                      </span>
                      <div>
                        <p className="font-medium text-brand-charcoal flex items-center gap-1.5"><Sparkles size={14} className="text-brand-pink" /> {a.name}</p>
                        <p className="text-sm text-brand-muted">+ {euro(a.price)} per set{a.additional_duration ? ` · +${a.additional_duration} min` : ""}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <button onClick={next} className="w-full mt-6 bg-brand-charcoal text-white font-semibold py-4 rounded-full flex items-center justify-center gap-2" data-testid="extras-continue-button">
              Doorgaan <ArrowRight size={18} />
            </button>
          </div>
        )}

        {step === "date" && (
          <div className="animate-fade-up" data-testid="booking-step-date">
            <div className="bg-white rounded-3xl border border-brand-border p-5 sm:p-6">
              <div className="flex items-center justify-between mb-5">
                <button onClick={() => setMonth(addMonths(month, -1))} disabled={isSameMonth(month, new Date())} className="h-9 w-9 rounded-full border border-brand-border flex items-center justify-center disabled:opacity-30" data-testid="cal-prev"><ChevronLeft size={18} /></button>
                <span className="font-display text-xl font-medium capitalize">{format(month, "MMMM yyyy", { locale: nl })}</span>
                <button onClick={() => setMonth(addMonths(month, 1))} className="h-9 w-9 rounded-full border border-brand-border flex items-center justify-center" data-testid="cal-next"><ChevronRight size={18} /></button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center text-xs text-brand-muted mb-2">
                {["ma", "di", "wo", "do", "vr", "za", "zo"].map((d) => <span key={d} className="py-1">{d}</span>)}
              </div>
              <div className="grid grid-cols-7 gap-1.5">
                {days.map((d, i) => {
                  const disabled = isBefore(d, tomorrow) || !isSameMonth(d, month);
                  const selected = date && isSameDay(d, date);
                  return (
                    <button key={i} disabled={disabled} onClick={() => { setDate(d); next(); }}
                      data-testid={isSameMonth(d, month) && !isBefore(d, tomorrow) ? `cal-day-${format(d, "yyyy-MM-dd")}` : undefined}
                      className={`aspect-square rounded-full flex items-center justify-center text-sm font-medium transition-colors ${!isSameMonth(d, month) ? "invisible" : disabled ? "text-brand-border cursor-not-allowed" : selected ? "bg-brand-charcoal text-white" : "text-brand-charcoal hover:bg-brand-blush"}`}>
                      {format(d, "d")}
                    </button>
                  );
                })}
              </div>
            </div>
            <p className="flex items-center gap-2 text-xs text-brand-muted mt-4 justify-center"><Info size={14} /> Online afspraken kunnen tot 24 uur vooraf geboekt worden.</p>
          </div>
        )}

        {step === "time" && (
          <div className="animate-fade-up" data-testid="booking-step-time">
            <p className="text-center text-brand-muted mb-5 capitalize">{date && format(date, "EEEE d MMMM yyyy", { locale: nl })}</p>
            {slotsLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="animate-spin text-brand-pink" size={30} /></div>
            ) : !durationConfigured ? (
              <EmptyState text="Deze behandeling is nog niet online boekbaar. Neem telefonisch contact op via 0465 00 03 31." />
            ) : slots.length === 0 ? (
              <EmptyState text="Helaas zijn er op deze datum geen beschikbare momenten. Kies een andere datum." action={() => setIdx(flow.indexOf("date"))} />
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                {slots.map((t) => (
                  <button key={t} onClick={() => { setTime(t); next(); }} data-testid={`slot-${t}`}
                    className={`py-3.5 rounded-2xl border font-medium transition-all ${time === t ? "bg-brand-charcoal text-white border-brand-charcoal" : "bg-white border-brand-border text-brand-charcoal hover:border-brand-pink"}`}>
                    {t}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {step === "details" && (
          <div className="animate-fade-up" data-testid="booking-step-details">
            <div className="bg-white rounded-3xl border border-brand-border p-5 sm:p-7 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Voornaam" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} testid="input-first-name" />
                <Field label="Achternaam" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} testid="input-last-name" />
              </div>
              <Field label="E-mailadres" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="input-email" />
              <Field label="Telefoonnummer" type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="input-phone" />
              <div>
                <label className="block text-sm font-medium text-brand-charcoal mb-1.5">Notitie (optioneel)</label>
                <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={3} data-testid="input-note" className="w-full rounded-2xl border border-brand-border px-4 py-3 text-brand-charcoal focus:border-brand-pink focus:outline-none resize-none" placeholder="Bijv. inspiratie of een wens voor je nagels" />
              </div>
            </div>
            <button disabled={!validDetails} onClick={next} className="w-full mt-5 bg-brand-charcoal text-white font-semibold py-4 rounded-full disabled:opacity-40 flex items-center justify-center gap-2" data-testid="details-continue-button">
              Doorgaan naar overzicht <ArrowRight size={18} />
            </button>
          </div>
        )}

        {step === "summary" && (
          <div className="animate-fade-up" data-testid="booking-step-summary">
            <div className="bg-white rounded-3xl border border-brand-border overflow-hidden">
              <div className="bg-brand-blush p-5">
                <p className="eyebrow">Jouw afspraak</p>
                <p className="font-display text-2xl font-semibold text-brand-charcoal mt-1">{service?.category} · {service?.name}</p>
              </div>
              <div className="p-5 space-y-3">
                <div className="flex justify-between text-sm"><span className="text-brand-muted">{service?.name}</span><span className="font-semibold text-brand-charcoal">{euro(service?.price)}</span></div>
                {selectedAddonObjs.map((a) => (
                  <div key={a.id} className="flex justify-between text-sm" data-testid={`summary-addon-${a.id}`}><span className="text-brand-muted">Extra: {a.name}</span><span className="font-semibold text-brand-charcoal">+ {euro(a.price)}</span></div>
                ))}
                <div className="flex justify-between pt-2 border-t border-brand-border"><span className="font-medium text-brand-charcoal">Totaal</span><span className="font-display text-xl font-semibold text-brand-charcoal" data-testid="summary-total">{euro(total)}</span></div>
                <div className="h-px bg-brand-border my-1" />
                <SummaryRow label="Datum" value={date && format(date, "EEEE d MMMM yyyy", { locale: nl })} />
                <SummaryRow label="Tijd" value={time} />
                <SummaryRow label="Naam" value={`${form.first_name} ${form.last_name}`} />
                <SummaryRow label="E-mail" value={form.email} />
                <SummaryRow label="Telefoon" value={form.phone} />
                {form.note && <SummaryRow label="Notitie" value={form.note} />}
              </div>
            </div>
            <div className="flex items-start gap-2.5 bg-white border border-brand-border rounded-2xl p-4 mt-4 text-sm text-brand-charcoal/80">
              <Info size={16} className="text-brand-pink mt-0.5 shrink-0" />
              Betaling gebeurt ter plaatse. Er is geen voorschot vereist. Je kan je afspraak tot 24 uur vooraf annuleren.
            </div>
            <button disabled={submitting} onClick={submit} className="w-full mt-5 bg-brand-charcoal text-white font-semibold py-4 rounded-full disabled:opacity-50 flex items-center justify-center gap-2" data-testid="booking-confirm-button">
              {submitting ? <Loader2 className="animate-spin" size={18} /> : <CalendarCheck size={18} />} Bevestig mijn afspraak
            </button>
          </div>
        )}

        {step === "success" && confirmed && (
          <div className="text-center py-10 animate-fade-up" data-testid="booking-success">
            <div className="h-20 w-20 rounded-full bg-brand-pink/20 flex items-center justify-center mx-auto mb-6"><CheckCircle2 className="text-brand-pink" size={48} /></div>
            <h1 className="font-display text-4xl font-semibold text-brand-charcoal mb-3">Je afspraak is bevestigd!</h1>
            <p className="text-brand-muted max-w-sm mx-auto">Je ontvangt een bevestiging van je afspraak via e-mail.</p>
            <div className="bg-white rounded-3xl border border-brand-border p-6 mt-8 text-left max-w-md mx-auto space-y-3">
              <SummaryRow label="Behandeling" value={`${confirmed.service_category} · ${confirmed.service_name}`} />
              {(confirmed.addons || []).length > 0 && <SummaryRow label="Extra" value={confirmed.addons.map((a) => a.name).join(", ")} />}
              <SummaryRow label="Datum" value={confirmed.date_display} />
              <SummaryRow label="Tijd" value={confirmed.start_time} />
              <SummaryRow label="Prijs" value={confirmed.price_display} />
              <div className="flex items-start gap-2 text-sm text-brand-muted pt-2 border-t border-brand-border"><MapPin size={15} className="text-brand-pink mt-0.5" /> Sint-Amelbergastraat 44, 9700 Mater</div>
            </div>
            <Link to="/" className="inline-block mt-8 bg-brand-charcoal text-white font-semibold px-8 py-3.5 rounded-full hover:bg-brand-pinkhover transition-colors">Terug naar website</Link>
          </div>
        )}
      </main>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-sm font-medium text-brand-charcoal mb-1.5">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full rounded-2xl border border-brand-border px-4 py-3 text-brand-charcoal focus:border-brand-pink focus:outline-none" />
    </div>
  );
}
function SummaryRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-brand-muted shrink-0">{label}</span>
      <span className="font-semibold text-brand-charcoal text-right capitalize">{value}</span>
    </div>
  );
}
function EmptyState({ text, action }) {
  return (
    <div className="text-center bg-white rounded-3xl border border-brand-border p-10">
      <p className="text-brand-charcoal/80">{text}</p>
      {action && <button onClick={action} className="mt-5 bg-brand-charcoal text-white font-semibold px-6 py-3 rounded-full">Kies andere datum</button>}
    </div>
  );
}
